import React from 'react';
import { useNavigate } from 'react-router-dom';
import { EventRepeat, CreditCard, CalendarToday } from '@mui/icons-material';
import {
  styleThousandCommas,
  planRecurrenceMap,
  getSubscriptionStatusColor,
  formatSubscriptionStatusLabel,
  isSubscriptionCancelAtPeriodEnd,
  resolveSubscriptionChargeDate,
  formatSubscriptionPlanTypeLabel,
  formatSubscriptionResourceTypeLabel,
  formatSubscriptionDate,
} from '../../utils/helpers';
import { SubscriptionSchema } from '../../data/types';

type SubscriptionCardProps = {
  subscription: SubscriptionSchema;
  /** Opens community resources modal when community_id is present */
  onViewResources?: (communityId: string) => void;
};

/**
 * Formats payment_method for display. Backend returns labels like "Visa •••• 4242";
 * still guards against raw Stripe IDs if one slips through.
 */
const formatPaymentMethodLabel = (paymentMethod?: string): string => {
  if (!paymentMethod) return '';
  if (paymentMethod.startsWith('pm_') || paymentMethod.startsWith('card_')) {
    return 'Card on file';
  }
  return paymentMethod;
};

/**
 * Mobile billing-focused card for a recurring (weekly/monthly/annual) subscription.
 * Emphasizes amount, cadence, and renewal timeline rather than membership-style chips.
 */
const SubscriptionCard: React.FC<SubscriptionCardProps> = ({
  subscription,
  onViewResources,
}) => {
  const navigate = useNavigate();
  const statusColors = getSubscriptionStatusColor(subscription.status || 'unknown');
  const cancelsAtPeriodEnd = isSubscriptionCancelAtPeriodEnd(subscription.cancel_at_period_end);
  const recurrenceLabel =
    subscription.plan_recurrence &&
    planRecurrenceMap[subscription.plan_recurrence as keyof typeof planRecurrenceMap];
  const chargeDate = resolveSubscriptionChargeDate(subscription);
  const communityName = subscription.name || 'Community';
  const planTitle = subscription.plan_name || 'Subscription';
  const planTypeLabel = formatSubscriptionPlanTypeLabel(subscription.plan_type);
  const resourceTypeLabel = formatSubscriptionResourceTypeLabel(subscription.resource_type);
  const paymentLabel = formatPaymentMethodLabel(subscription.payment_method);
  const associatedResourceNames = (subscription.associated_resources || [])
    .map((resource) => resource.resource_name)
    .filter(Boolean) as string[];

  const coverageParts = [
    resourceTypeLabel,
    subscription.resource_name,
    subscription.is_fulltime_hours
      ? '24/7 Access'
      : (subscription.plan_max_hours != null && subscription.plan_type !== 'membership')
        ? `Up to ${styleThousandCommas(subscription.plan_max_hours)} hrs`
        : null,
    planTypeLabel || null,
  ].filter(Boolean);

  return (
    <div className="border border-[#E9E9E9] rounded-xl shadow-sm overflow-hidden bg-white">
      <div className="p-4 flex flex-col gap-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-11 h-11 flex items-center justify-center bg-[#F7FBFC] rounded-lg border border-[#E9E9E9]">
            {subscription.logo ? (
              <img
                src={subscription.logo}
                alt={communityName}
                className="h-7 w-7 object-contain"
              />
            ) : (
              <span className="font-raleway font-bold text-sm text-primaryDark-0">
                {communityName.charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-raleway font-semibold text-base text-black break-words">{communityName}</p>
            <p className="font-open-sans text-xs text-[#8A8A8A] mt-0.5">{planTitle}</p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {subscription.status && (
                <span
                  className="px-2 py-0.5 rounded-md text-[11px] font-raleway font-semibold"
                  style={{
                    backgroundColor: statusColors.bgColor,
                    color: statusColors.primaryTextColor,
                  }}
                >
                  {formatSubscriptionStatusLabel(subscription.status)}
                </span>
              )}
              {cancelsAtPeriodEnd && (
                <span className="px-2 py-0.5 rounded-md text-[11px] font-raleway font-semibold bg-[#faeeee] text-[#CE5D5D]">
                  Ends this period
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-lg bg-[#F7FBFC] border border-[#E9E9E9] px-4 py-3">
          <p className="text-[11px] uppercase tracking-wide font-raleway font-semibold text-[#8A8A8A] mb-1">
            Recurring amount
          </p>
          <p className="font-raleway font-bold text-2xl text-primaryDark-0 leading-none">
            ${styleThousandCommas(subscription.plan_rate || 0)}
            {recurrenceLabel && (
              <span className="text-sm font-semibold text-[#4D4D4D] ml-1">
                / {recurrenceLabel}
              </span>
            )}
          </p>
          <div className="flex items-center gap-1.5 mt-3 text-sm font-raleway font-semibold text-primaryDark-0">
            <EventRepeat className="w-4 h-4 flex-shrink-0 text-primaryDark-0/70" />
            <span className="truncate">
              {cancelsAtPeriodEnd ? 'Access through' : 'Next charge'} {chargeDate}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2.5">
          {subscription.last_charge_date && (
            <div className="rounded-lg border border-[#E9E9E9] bg-[#FAFAFA] px-3 py-2.5">
              <p className="uppercase tracking-wide font-raleway font-semibold text-[#8A8A8A] text-[10px]">
                Last charged
              </p>
              <p className="font-raleway font-semibold text-sm text-[#4D4D4D] mt-1">
                {formatSubscriptionDate(subscription.last_charge_date)}
              </p>
            </div>
          )}
          {subscription.started && (
            <div className="rounded-lg border border-[#E9E9E9] bg-[#F7FBFC] px-3 py-2.5">
              <div className="flex items-center gap-1 mb-1">
                <CalendarToday className="!w-3 !h-3 text-primaryDark-0/55" />
                <p className="uppercase tracking-wide font-raleway font-semibold text-[#8A8A8A] text-[10px]">
                  Subscribed since
                </p>
              </div>
              <p className="font-raleway font-semibold text-sm text-primaryDark-0">
                {formatSubscriptionDate(subscription.started)}
              </p>
            </div>
          )}
          {paymentLabel && (
            <div className="col-span-2 rounded-lg border border-[#E9E9E9] bg-[#FAFAFA] px-3 py-2.5">
              <p className="uppercase tracking-wide font-raleway font-semibold text-[#8A8A8A] text-[10px]">
                Payment method
              </p>
              <p className="font-raleway font-semibold text-sm text-[#4D4D4D] mt-1 inline-flex items-center gap-1.5">
                <CreditCard className="!w-3.5 !h-3.5 text-primaryDark-0/60" />
                {paymentLabel}
              </p>
            </div>
          )}
        </div>

        {coverageParts.length > 0 && (
          <p className="text-xs font-open-sans text-[#4D4D4D]">
            {coverageParts.join(' · ')}
          </p>
        )}

        {associatedResourceNames.length > 0 && (
          <p className="text-xs font-open-sans text-[#8A8A8A]">
            Includes <span className="text-primaryDark-400">{associatedResourceNames.join(', ')}</span>
          </p>
        )}

        <div className="flex flex-col gap-2 pt-1">
          <button
            type="button"
            onClick={() => navigate('/wallet')}
            className="w-full bg-primaryDark-0 text-white rounded-lg px-4 py-2.5 text-sm font-raleway font-medium hover:bg-primaryDark-300"
          >
            Manage Billing
          </button>
          {subscription.community_id && onViewResources && (
            <button
              type="button"
              onClick={() => onViewResources(subscription.community_id || '')}
              className="w-full bg-white border border-[#E9E9E9] text-[#4D4D4D] rounded-lg px-4 py-2.5 text-sm font-raleway font-medium hover:bg-gray-50"
            >
              View Resources
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionCard;
